import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import { ToastProvider } from "@/lib/toast";
import { emitToast } from "@/lib/toast-context";
import { HttpError } from "@/lib/http";
import "./index.css";

/**
 * Every mutation failure is visible by default.
 *
 * 20 of the portal's 43 mutations had no `onError`, so pausing an outbox
 * consumer, replaying a DLQ, cancelling a reindex or toggling a feature flag
 * could fail and show nothing — on an incident console, leaving you to believe
 * the thing you clicked had happened. Handling it in the MutationCache means a
 * mutation has to opt *out* of being seen, rather than opt in.
 *
 * A page that already reports the failure its own way still does; this only
 * adds the floor.
 */
const mutationCache = new MutationCache({
  onError: (error) => {
    // 401 is already handled globally: the token is cleared and the operator
    // is sent to the login screen, so a toast about it would be noise on a
    // page they are leaving anyway.
    if (error instanceof HttpError && error.status === 401) return;

    const detail =
      error instanceof HttpError
        ? (error.body as { message?: string; error?: string } | null)
        : null;

    emitToast(
      detail?.message ??
        detail?.error ??
        (error instanceof Error ? error.message : "That action failed."),
    );
  },
});

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
